import { Injectable, inject } from '@angular/core';
import { UtilsService } from '../utils/utils.service';
import { GridColumn, ViewFilterRule, ViewFiltersPayload, ViewColumnsPayload } from '../../models/work-package-view.model';
import {
  FilterOperatorDef,
  getOperatorsForType,
  getDefaultOperator,
  isBetweenOperator,
  isMultiValueOperator,
  isNoValueOperator,
} from '../../models/filter.model';
import { FilterItem } from '../../components/saved-views/saved-views.component';
import {
  DataType,
  InputType,
  ColumnType,
  getUserFieldNames,
  getExcludeFields,
} from './views.enums';

/**
 * Service for handling filter and column operations across Views components.
 * Consolidates duplicated logic from saved-views and create-view-dialog components.
 */
@Injectable({
  providedIn: 'root',
})
export class ViewsFilterService {
  private utilsService = inject(UtilsService);


  /**
   * Convert filter rules from API to FilterItem array
   */
  /**
   * Extract the shared milestone ID from a saved rules array.
   * Returns the numeric ID from the milestoneId rule if present, otherwise null.
   */
  extractSharedMilestoneId(rules: ViewFilterRule[]): number | null {
    if (!rules || !Array.isArray(rules)) return null;
    const idRule = rules.find(r => r.type === ColumnType.MILESTONE && r.field === 'milestoneId');
    const id = Number(idRule?.value);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  private localDateToUtcIso(value: string, dataType: string): string {
    return this.utilsService.convertFilterDateToUTC(value, dataType as 'date' | 'datetime');
  }

  private utcIsoToLocalInputValue(value: string, dataType: string): string {
    return this.utilsService.convertUTCFilterDateToLocal(value, dataType as 'date' | 'datetime');
  }

  filtersFromRules(rules: ViewFilterRule[], availableColumns: GridColumn[]): FilterItem[] {
    if (!rules || !Array.isArray(rules)) {
      return [];
    }

    return rules
      // Skip both the new-format milestoneId sentinel and the old-format milestoneName sentinel
      .filter(rule => !(rule.type === ColumnType.MILESTONE && (rule.field === 'milestoneId' || rule.field === 'milestoneName')))
      .map((rule: ViewFilterRule) => {
        const column = availableColumns.find(col => col.field === rule.field);
        if (!column) {
          return null;
        }

        const isDate = column.dataType === DataType.DATE || column.dataType === DataType.DATETIME;
        const toInput = (v: unknown) => isDate
          ? this.utcIsoToLocalInputValue(String(v ?? ''), column.dataType!)
          : String(v ?? '');

        return {
          column,
          operator: rule.op,
          value: toInput(rule.value),
          valueTo: toInput(rule.valueTo),
          multiValues: Array.isArray(rule.values) ? rule.values.map(v => String(v)) : [],
        } as FilterItem;
      })
      .filter((item): item is FilterItem => item !== null);
  }

  /**
   * Build API-compatible filters payload from applied filters
   */
  buildFiltersPayload(
    appliedFilters: FilterItem[],
    filterLogic: 'AND' | 'OR',
    sharedMilestoneId?: number,
  ): ViewFiltersPayload {
    const rules: ViewFilterRule[] = [];

    // Prepend a single milestoneId rule if any milestone filter exists
    const hasMilestoneFilter = appliedFilters.some(
      f => (f.column.columnType || ColumnType.STANDARD) === ColumnType.MILESTONE,
    );
    if (hasMilestoneFilter && sharedMilestoneId) {
      rules.push({
        type: ColumnType.MILESTONE,
        field: 'milestoneId',
        op: 'eq',
        value: sharedMilestoneId,
      });
    }

    for (const filter of appliedFilters) {
      const operator = filter.operator;
      const columnType = filter.column.columnType || ColumnType.STANDARD;
      const dataType = filter.column.dataType ?? '';
      const isDate = dataType === DataType.DATE || dataType === DataType.DATETIME;
      const toUtc = (v: string) => isDate ? this.localDateToUtcIso(v, dataType) : v;

      if (isNoValueOperator(operator)) {
        rules.push({ type: columnType, field: filter.column.field, op: operator });
      } else if (isBetweenOperator(operator)) {
        if (!filter.value && !filter.valueTo) continue;
        rules.push({
          type: columnType,
          field: filter.column.field,
          op: operator,
          value: toUtc(String(filter.value ?? '')),
          valueTo: toUtc(filter.valueTo || ''),
        });
      } else if (isMultiValueOperator(operator)) {
        if (!filter.multiValues?.length) continue;
        rules.push({
          type: columnType,
          field: filter.column.field,
          op: operator,
          values: [...filter.multiValues],
        });
      } else {
        if (!filter.value) continue;
        rules.push({ type: columnType, field: filter.column.field, op: operator, value: toUtc(String(filter.value)) });
      }
    }

    return { logic: filterLogic, rules };
  }

  /**
   * Build API-compatible columns payload from selected columns
   */
  buildColumnsPayload(selectedColumns: Set<string>, availableColumns: GridColumn[]): ViewColumnsPayload {
    const standard: string[] = [];
    const custom: string[] = [];
    const milestone: string[] = [];

    for (const columnField of selectedColumns) {
      const column = availableColumns.find(c => c.field === columnField);
      if (column) {
        if (column.columnType === ColumnType.CUSTOM) {
          custom.push(columnField);
        } else if (column.columnType === ColumnType.MILESTONE) {
          milestone.push(columnField);
        } else {
          standard.push(columnField);
        }
      }
    }

    const payload: ViewColumnsPayload = { standard, custom };
    if (milestone.length > 0) {
      payload.milestone = milestone;
    }
    return payload;
  }

  /**
   * Get columns available for filtering
   * Excludes already applied filters and non-meaningful ID fields
   */
  getAvailableColumnsForFilter(
    allColumns: GridColumn[],
    appliedFilters: FilterItem[]
  ): GridColumn[] {
    const excludeFields = getExcludeFields();
    const appliedColumnFields = appliedFilters.map(f => f.column.field);
    return allColumns.filter(
      col =>
        col.allowFiltering !== false &&
        !appliedColumnFields.includes(col.field) &&
        !excludeFields.includes(col.field?.toLowerCase() || ''),
    );
  }

  /**
   * Get dynamic operators for a column.
   * For user fields, lookup fields (status/type), and state fields, use dropdown operators.
   * Custom fields do not support null-check operators (isnull/isnotnull).
   */
  getOperatorsForColumn(column: GridColumn, isUserField: boolean): FilterOperatorDef[] {
    let operators: FilterOperatorDef[];

    if (
      isUserField ||
      column.field?.toLowerCase() === 'state' ||
      column.field?.toLowerCase() === 'tags' ||
      (column.lookupOptions && column.lookupOptions.length > 0)
    ) {
      operators = getOperatorsForType(DataType.DROPDOWN);
    } else {
      operators = getOperatorsForType(column.dataType || 'string');
    }
    return operators;
  }

  /**
   * Get input type based on column dataType
   */
  getInputType(column: GridColumn): string {
    switch (column.dataType) {
      case DataType.NUMBER:
        return InputType.NUMBER;
      case DataType.DATE:
        return InputType.DATE;
      case DataType.DATETIME:
        return InputType.DATETIME_LOCAL;
      default:
        return InputType.TEXT;
    }
  }

  /**
   * Get default operator for adding a new filter
   * Considers user fields specially
   */
  getDefaultOperatorForColumn(column: GridColumn, isUserField: boolean): string {
    // User fields, state fields, and lookup fields should use dropdown default operator
    const isState = column.field?.toLowerCase() === 'state';
    const isTags = column.field?.toLowerCase() === 'tags';
    const isLookup = !!(column.lookupOptions && column.lookupOptions.length > 0);
    const dataType = (isUserField || isState || isTags || isLookup) ? DataType.DROPDOWN : (column.dataType || 'string');
    return getDefaultOperator(dataType);
  }

  /**
   * Check if operator is a 'between' type (requires two values)
   */
  isBetween(operator: string): boolean {
    return isBetweenOperator(operator);
  }

  /**
   * Check if operator is a multi-value type (in, notin)
   */
  isMulti(operator: string): boolean {
    return isMultiValueOperator(operator);
  }

  /**
   * Check if operator requires no value input (isnull, isnotnull)
   */
  isNoValue(operator: string): boolean {
    return isNoValueOperator(operator);
  }

  /**
   * Check if a column is a user field
   */
  isUserField(column: GridColumn): boolean {
    const userFieldNames = getUserFieldNames();
    return userFieldNames.includes(column.field?.toLowerCase() || '');
  }

  /**
   * Check if a column is a numeric type
   */
  isNumberField(column: GridColumn): boolean {
    return column.dataType === DataType.NUMBER;
  }

  isDateField(column: GridColumn): boolean {
    return column.dataType === DataType.DATE || column.dataType === DataType.DATETIME;
  }

  /**
   * Get a context-aware placeholder for a filter value input.
   * Mirrors the milestone-tracker's getPlaceholder() approach.
   */
  getPlaceholder(column: GridColumn, boundary?: 'start' | 'end'): string {
    switch (column.dataType) {
      case DataType.NUMBER:
        if (boundary === 'start') return 'e.g. 0';
        if (boundary === 'end') return 'e.g. 100';
        return 'e.g. 101';
      case DataType.DATE:
        if (boundary === 'start') return 'Start date';
        if (boundary === 'end') return 'End date';
        return 'Select date';
      case DataType.DATETIME:
        if (boundary === 'start') return 'Start';
        if (boundary === 'end') return 'End';
        return 'Select date & time';
      default:
        return 'Value';
    }
  }

  /**
   * Check if a column is a dropdown type
   */
  isDropdownColumn(column: GridColumn, userOptions: unknown[] = []): boolean {
    // Check if it's a user field
    if (this.isUserField(column) && userOptions.length > 0) {
      return true;
    }

    // Check if it's a lookup field (status, type, etc.)
    if (column.dataType === DataType.DROPDOWN && !!column.lookupOptions && column.lookupOptions.length > 0) {
      return true;
    }

    // Or check if it's a standard dropdown column
    return (
      column.dataType === DataType.DROPDOWN &&
      !!column.options &&
      column.options.length > 0
    );
  }

  /**
   * Initialize selected columns from view columns
   */
  initializeSelectedColumns(
    viewColumns: { standard?: string[]; custom?: string[]; milestone?: string[] } | null | undefined,
    availableColumns: GridColumn[]
  ): Set<string> {
    const selectedFields = new Set<string>();

    if (viewColumns?.standard) {
      viewColumns.standard.forEach(field => selectedFields.add(field));
    }
    if (viewColumns?.custom) {
      viewColumns.custom.forEach(field => selectedFields.add(field));
    }
    if (viewColumns?.milestone) {
      viewColumns.milestone.forEach(field => selectedFields.add(field));
    }

    // If no columns selected in view, select all
    return selectedFields.size > 0
      ? selectedFields
      : new Set(availableColumns.map(col => col.field));
  }
}
